import { useState, useEffect, useCallback, useRef } from "react";
import { Plus, CalendarDays, Search, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDate, todayIST } from "@/lib/format";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STATUSES = ["booked", "confirmed", "completed", "cancelled", "no-show", "rescheduled"];

export default function Appointments() {
  const [date, setDate] = useState(todayIST());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointments", { params: { date } });
      setItems(data.items);
    } finally { setLoading(false); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/appointments/${id}`, { status }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Appointments" subtitle={fmtDate(date)} testid="appointments-page">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="appt-date" />
        <Button onClick={() => setOpen(true)} data-testid="add-appt-btn" className="bg-bk-green hover:bg-bk-greenDark"><Plus className="h-4 w-4 mr-1" /> New</Button>
      </PageHeader>

      {loading ? <Loading /> : items.length === 0 ? (
        <EmptyState icon={CalendarDays} title="No appointments" subtitle="Book an appointment for this date." />
      ) : (
        <div className="grid gap-2.5 md:grid-cols-2 lg:grid-cols-3">
          {items.map((a) => (
            <Card key={a.id} className="p-4" data-testid={`appt-${a.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-800">{a.patient_name}</p>
                  <p className="text-xs text-slate-500">{a.time} · <span className="capitalize">{a.service}</span> · {a.type}</p>
                </div>
                <StatusBadge status={a.status} />
              </div>
              <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                <SelectTrigger className="h-8 mt-3 capitalize"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </Card>
          ))}
        </div>
      )}

      <ApptDialog open={open} onOpenChange={setOpen} date={date} onSaved={load} />
    </div>
  );
}

function ApptDialog({ open, onOpenChange, date, onSaved }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [time, setTime] = useState("10:00");
  const [service, setService] = useState("clinic");
  const [type, setType] = useState("consultation");
  const [d, setD] = useState(date);
  const [loading, setLoading] = useState(false);
  const timer = useRef();

  useEffect(() => { if (open) { setQ(""); setResults([]); setPatient(null); setD(date); } }, [open, date]);
  useEffect(() => {
    clearTimeout(timer.current);
    if (q.length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      const { data } = await api.get("/patients", { params: { search: q, limit: 6 } });
      setResults(data.items);
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  const submit = async () => {
    if (!patient) { toast.error("Select a patient"); return; }
    setLoading(true);
    try {
      await api.post("/appointments", { patient_id: patient.id, date: d, time, service, type });
      toast.success("Appointment booked");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">New Appointment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!patient ? (
            <div className="space-y-1.5">
              <Label>Patient</Label>
              <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patient..." className="pl-9" data-testid="appt-patient-search" /></div>
              {results.map((r) => (
                <button key={r.id} onClick={() => setPatient(r)} className="w-full text-left px-3 py-2 rounded-md border hover:bg-bk-lightGreen text-sm">{r.name} · {r.patient_id}</button>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-md border px-3 py-2 bg-bk-lightGreen/40">
              <span className="text-sm font-medium">{patient.name} · {patient.patient_id}</span>
              <Button variant="ghost" size="sm" className="h-7" onClick={() => setPatient(null)}>Change</Button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={d} onChange={(e) => setD(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Time</Label><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Service</Label>
              <Select value={service} onValueChange={setService}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="clinic">Clinic</SelectItem><SelectItem value="home">Home</SelectItem></SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={type} onValueChange={setType}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="consultation">Consultation</SelectItem><SelectItem value="session">Session</SelectItem><SelectItem value="review">Review</SelectItem></SelectContent></Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="bg-bk-green hover:bg-bk-greenDark">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Book"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
